// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow, Link } from "@insite/mobius/index";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import dataTableSharedStyles from "@stanion/App/SharedStyle/MyAccount/List/DataTableSharedStyles";
import { EventListPageContext } from "@stanion/App/Store/Selectors/xAllEventsRequestSelector";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface AllEventsListStyles {
    container?: InjectableCss;
    dataTable?: DataTableProps;
    header?: DataTableHeaderProps;
    cell?: DataTableCellProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

const allEventsListStyles: AllEventsListStyles = {
    noResultsContainer: {
        ...dataTableSharedStyles.noResultsContainer,
    },
    noResultsText: {
        ...dataTableSharedStyles.noResultsText,
    },
    spinner: {
        ...dataTableSharedStyles.spinner,
    },
    centeringWrapper: {
        ...dataTableSharedStyles.centeringWrapper,
    },
    dataTable: {
        ...dataTableSharedStyles.dataTable,
    },
};

const styles = allEventsListStyles;
const AllEventsListTable: FC<Props> = ({ language }) => {
    const allEvents = useContext(EventListPageContext);

    if (allEvents.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!allEvents.value) {
        return null;
    }

    if (allEvents?.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography
                    as="p"
                    {...styles.noResultsText}
                    data-test-selector="invoiceDetails_outOfStockOrder_noOrdersFound"
                >
                    {translate("No Events found")}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable}>
                <DataTableHead>
                    <DataTableHeader {...styles.header}>{translate("Date")}</DataTableHeader>
                    <DataTableHeader {...styles.header}>{translate("Location")}</DataTableHeader>
                    <DataTableHeader {...styles.header}>{translate("Title")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {allEvents?.value &&
                        allEvents?.value.map(event => {
                            const startDate = getLocalizedDateTime({
                                dateTime: new Date(event.eventStartDate),
                                language,
                                options: {
                                    month: "short",
                                    day: "numeric",
                                },
                            });
                            const endDate = getLocalizedDateTime({
                                dateTime: new Date(event.eventEndDate),
                                language,
                                options: {
                                    month: "short",
                                    day: "numeric",
                                },
                            });
                            return (
                                <DataTableRow key={event.id}>
                                    <DataTableCell {...styles.cell}>
                                        <Link href={event.url}>
                                            {startDate === endDate ? startDate : `${startDate} - ${endDate}`}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell {...styles.cell}>{event.eventLocation}</DataTableCell>
                                    <DataTableCell {...styles.cell}>{event.eventTitle}</DataTableCell>
                                </DataTableRow>
                            );
                        })}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(AllEventsListTable),
    definition: {
        displayName: "All Events List Table",
        allowedContexts: ["AllEventsPage" as any],
        group: "All Events" as any,
    },
};

export default widgetModule;
